import { httpBatchLink } from "@trpc/client/links/httpBatchLink";
import { httpLink } from "@trpc/client/links/httpLink";
import { loggerLink } from "@trpc/client/links/loggerLink";
import { splitLink } from "@trpc/client/links/splitLink";
import { createTRPCNext as _createTRPCNext } from "@trpc/next";
import type { NextPageContext } from "next";
import superjson from "superjson";

import type { AppRouter } from "../server/router";

/**
 * Extend `NextPageContext` with meta data that can be picked up by `responseMeta()` when server-side rendering
 */
export interface SSRContext extends NextPageContext {
  /**
   * Set HTTP Status code
   * @example
   * const utils = trpc.useContext();
   * if (utils.ssrContext) {
   *   utils.ssrContext.status = 404;
   * }
   */
  status?: number;
}

export function createTRPCNext({ baseUrl }: { baseUrl: string }) {
  const getHeaders = (ctx?: SSRContext) => {
    if (ctx?.req) {
      // To use SSR properly, you need to forward the client's headers to the server
      // This is so you can pass through things like cookies when we're server-side rendering

      // If you're using Node 18, omit the "connection" header
      const { connection: _connection, ...headers } = ctx.req.headers;
      return {
        ...headers,
        // Optional: inform server that it's an SSR request
        "x-ssr": "1",
      };
    }
    return {};
  };

  return _createTRPCNext<AppRouter, SSRContext>({
    transformer: superjson,
    ssr: false,
    config({ ctx }) {
      const linkParams = {
        url: `${baseUrl}/api/trpc`,
        headers: () => getHeaders(ctx),
        transformer: superjson,
      };

      return {
        links: [
          loggerLink({
            enabled: (opts) =>
              process.env.NODE_ENV === "development" ||
              (opts.direction === "down" && opts.result instanceof Error),
          }),
          splitLink({
            condition(op) {
              return op.context.skipBatch === true;
            },
            true: httpLink(linkParams),
            false: httpBatchLink(linkParams),
          }),
        ],
      };
    },
  });
}
